import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { CrcPipe } from '../../../shared/pipes/crc.pipe';

import { ButtonComponent } from '../../../shared/ui/button/button.component';
import { CartService } from '../../../shared/services/cart.service';
import { DeliveryPreferenceService } from '../../../shared/services/delivery-preference.service';
import { AuthService } from '../../../shared/services/auth.service';
import { PointsService } from '../../../shared/services/points.service';
import { Preorder } from '../../../shared/models/cart.model';
import { toAmount } from '../../../shared/utils/amount';
import { isMondayInCostaRica } from './checkout-success-date.util';
import { CHECKOUT_COPY } from '../checkout-copy';

type CheckoutSuccessNavigationState = {
  preorder?: Preorder;
  preorderHasSingles?: boolean;
};

@Component({
  selector: 'app-checkout-success-page',
  imports: [CrcPipe, ButtonComponent, RouterLink],
  templateUrl: './checkout-success-page.component.html',
  styleUrl: './checkout-success-page.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class CheckoutSuccessPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly cartService = inject(CartService);
  private readonly deliveryPref = inject(DeliveryPreferenceService);
  private readonly authService = inject(AuthService);
  private readonly pointsService = inject(PointsService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly preorder = signal<Preorder | null>(null);
  protected readonly loading = signal(true);
  protected readonly notFound = signal(false);
  private readonly preorderHasSinglesFromState = signal<boolean | null>(null);

  /** El checkout se completó un lunes según la hora oficial de Costa Rica. */
  protected readonly isMonday = computed(() => {
    const createdAt = this.preorder()?.created_at;
    return createdAt ? isMondayInCostaRica(createdAt) : false;
  });

  constructor() {
    // Estrategia híbrida:
    //  1. Si llegamos vía router state (recién creado el preorder), lo
    //     usamos sin pegarle al backend. Camino feliz, instantáneo.
    //  2. Si no hay state (refresh, link directo, llegada desde email),
    //     refetchamos por token.
    const stateNav = this.router.getCurrentNavigation()?.extras?.state as
      | CheckoutSuccessNavigationState
      | null
      | undefined;
    const fromState = stateNav?.preorder;
    if (typeof stateNav?.preorderHasSingles === 'boolean') {
      this.preorderHasSinglesFromState.set(stateNav.preorderHasSingles);
    }

    if (fromState && typeof fromState === 'object' && 'token' in fromState) {
      this.applyPreorder(fromState);
      return;
    }

    const token = this.route.snapshot.paramMap.get('token');
    if (!token) {
      this.notFound.set(true);
      this.loading.set(false);
      return;
    }

    this.cartService
      .getPreorderByToken(token)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (p) => {
          this.applyPreorder(p);
        },
        error: () => {
          this.notFound.set(true);
          this.loading.set(false);
        },
      });
  }

  /**
   * Publica el preorder confirmado y recuerda su método de envío como
   * preferencia para el próximo checkout. Único punto común a los tres flujos
   * de pago (SINPE/transferencia, tarjeta ONVO, stub) donde el pedido ya está
   * materializado y `delivery_method` es fiable — también el punto único para
   * refrescar el saldo de Perrón Points tras la confirmación (solo con sesión).
   */
  private applyPreorder(p: Preorder): void {
    this.preorder.set(p);
    this.deliveryPref.setLastMethod(p.delivery_method);
    this.loading.set(false);
    if (this.authService.isAuthenticated()) {
      this.pointsService.refresh();
    }
  }

  /** Construye el string completo de dirección a partir de los 5 nuevos. */
  protected fullAddress(p: Preorder): string {
    const parts = [p.directions, p.hood, p.district, p.canton, p.province]
      .map((s) => s?.trim())
      .filter((s): s is string => !!s);
    if (parts.length > 0) return parts.join(', ');
    return p.address ?? '';
  }

  protected preorderHasSingles(p: Preorder): boolean {
    const fromState = this.preorderHasSinglesFromState();
    if (fromState !== null) return fromState;
    return (p.lines ?? []).some((line) => line.product?.product_type === 'single');
  }

  /** Re-export local de `toAmount` para que el template lo invoque sin import extra. */
  protected readonly toAmount = toAmount;

  /** Copy de esta pantalla (v3): mensaje extra de horario, solo retiro en tienda. */
  protected readonly checkoutSuccessCopy = CHECKOUT_COPY.checkoutSuccess;
}
